import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      className={`px-2 py-1 text-xs rounded transition-colors select-none ${
        active
          ? 'bg-blue-600 text-white'
          : 'text-gray-400 hover:text-app-fg hover:bg-app-border'
      }`}
    >
      {children}
    </button>
  );
}

export default function ScriptEditor({ value, onSave, onClose }) {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || '',
    autofocus: 'end',
    editorProps: {
      attributes: {
        class: 'tiptap-content outline-none',
      },
    },
  });

  // Ctrl+S saves
  function handleKeyDown(e) {
    if (e.key === 'Escape') { onClose(); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      doSave();
    }
  }

  function doSave() {
    if (!editor) return;
    const html = editor.getHTML();
    // Treat empty doc as null
    const isEmpty = editor.isEmpty;
    onSave(isEmpty ? null : html);
    onClose();
  }

  if (!editor) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-app-header border border-app-border rounded-lg flex flex-col"
        style={{ width: '660px', maxHeight: '75vh' }}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Toolbar */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-app-border flex-wrap flex-shrink-0">
          <ToolbarBtn
            onClick={() => editor.chain().focus().setParagraph().run()}
            active={editor.isActive('paragraph') && !editor.isActive('heading')}
            title="Normal text"
          >
            Normal
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive('heading', { level: 1 })}
            title="Heading"
          >
            Heading
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            title="Subheading"
          >
            Sub
          </ToolbarBtn>

          <div className="w-px h-4 bg-app-border mx-1 flex-shrink-0" />

          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            title="Underline (Ctrl+U)"
          >
            <span style={{ textDecoration: 'underline' }}>U</span>
          </ToolbarBtn>

          <div className="w-px h-4 bg-app-border mx-1 flex-shrink-0" />

          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Bullet list"
          >
            ≡ List
          </ToolbarBtn>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: '180px' }}>
          <EditorContent editor={editor} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-app-border flex-shrink-0">
          <span className="text-xs text-gray-600">Ctrl+S to save · Esc to cancel</span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-app-fg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={doSave}
              className="px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
